import React, { useState, useEffect } from "react";
import {
  createVocabularyGroup,
  updateVocabularyGroup,
  fetchVocabularyGroupById,
  fetchVocabulariesForDropdown,
} from "./adminVocabularyGroupService";
import { useToast } from "../../../common/toast/ToastContext";
import ImagePickerModal from "../../media_images/ImagePickerModal";
import "../../styles/content.css";

const ROOT = process.env.REACT_APP_BASE_URL_ROOT;
const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api(?:\/admin)?\/media-images\//, "/api/public/media-images/");
  const src = pub.startsWith("http") ? pub : `${ROOT}${pub}`;
  console.log("[VocabularyGroupForm] img raw:", url, "→ resolved:", src);
  return src;
};

const VocabularyGroupForm = ({ group, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const isEdit = !!group;

  const [formData, setFormData] = useState({ name: "", description: "", vocabularyIds: [], imageUrl: "" });
  const [vocabularies, setVocabularies] = useState([]);
  const [selectedVocabularies, setSelectedVocabularies] = useState([]);
  const [vocabularySearch, setVocabularySearch] = useState("");
  const [showVocabularyDropdown, setShowVocabularyDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingVocabularies, setLoadingVocabularies] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);

  useEffect(() => {
    if (isEdit) loadGroupData();
    loadVocabularies();
  }, []);

  const loadGroupData = async () => {
    const result = await fetchVocabularyGroupById(group.id);
    if (result.status === 200) {
      const data = result.data;
      setFormData({
        name: data.name || "",
        description: data.description || "",
        vocabularyIds: data.vocabularies?.map((v) => v.id) || [],
        imageUrl: data.imageUrl || "",
      });
      setSelectedVocabularies(data.vocabularies || []);
    }
  };

  const loadVocabularies = async (search = "") => {
    setLoadingVocabularies(true);
    const params = { page: 0, size: 50 };
    if (search.trim()) params.search = search.trim();
    const result = await fetchVocabulariesForDropdown(params);
    if (result.status === 200) setVocabularies(result.data.content || []);
    setLoadingVocabularies(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVocabularySearch = (e) => {
    const value = e.target.value;
    setVocabularySearch(value);
    loadVocabularies(value);
  };

  const handleVocabularySelect = (vocabulary) => {
    if (!formData.vocabularyIds.includes(vocabulary.id)) {
      setFormData((prev) => ({ ...prev, vocabularyIds: [...prev.vocabularyIds, vocabulary.id] }));
      setSelectedVocabularies((prev) => [...prev, vocabulary]);
    }
    setVocabularySearch("");
    setShowVocabularyDropdown(false);
  };

  const handleVocabularyRemove = (vocabularyId) => {
    setFormData((prev) => ({ ...prev, vocabularyIds: prev.vocabularyIds.filter((id) => id !== vocabularyId) }));
    setSelectedVocabularies((prev) => prev.filter((v) => v.id !== vocabularyId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { showToast("Name is required", "error"); return; }
    if (formData.vocabularyIds.length === 0) { showToast("At least one vocabulary is required", "error"); return; }

    setLoading(true);
    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      vocabularyIds: formData.vocabularyIds,
      imageUrl: formData.imageUrl || null,
    };

    const result = isEdit
      ? await updateVocabularyGroup(group.id, payload)
      : await createVocabularyGroup(payload);

    if (result.status === 200 || result.status === 201) {
      showToast(result.message, "success");
      onSuccess();
    } else {
      showToast(result.message, "error");
    }
    setLoading(false);
  };

  const filteredVocabularies = vocabularies.filter(
    (v) => !formData.vocabularyIds.includes(v.id) && v.word.toLowerCase().includes(vocabularySearch.toLowerCase())
  );

  const capitalizePos = (pos) => pos ? pos.charAt(0).toUpperCase() + pos.slice(1).toLowerCase() : pos;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 580, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? "Edit Vocabulary Group" : "Create Vocabulary Group"}
          </h2>
          <button onClick={onCancel} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1 }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div className="form-group">
            <label>Name <span style={{ color: "red" }}>*</span></label>
            <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter group name" required />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} placeholder="Enter description (optional)" style={{ width: "100%", resize: "vertical" }} />
          </div>

          {/* Image */}
          <div className="form-group">
            <label>Image (optional)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {formData.imageUrl ? (
                <img src={toPublicImgSrc(formData.imageUrl)} alt="selected"
                  style={{ width: 60, height: 60, objectFit: "contain", background: "#f5f5f5", borderRadius: 8, border: "1px solid #ddd" }} />
              ) : (
                <div style={{ width: 60, height: 60, background: "#f0f0f0", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#aaa" }}>No image</div>
              )}
              <button type="button" className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setShowImagePicker(true)}>Pick Image</button>
              {formData.imageUrl && (
                <button type="button" className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setFormData({ ...formData, imageUrl: "" })}>Clear</button>
              )}
            </div>
          </div>

          {/* Vocabulary Selector */}
          <div className="form-group">
            <label>Vocabularies <span style={{ color: "red" }}>*</span></label>

            {selectedVocabularies.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {selectedVocabularies.map((vocab) => (
                  <span key={vocab.id} style={{ display: "inline-flex", alignItems: "center", background: "#dbeafe", color: "#1e40af", padding: "3px 10px", borderRadius: 20, fontSize: 13, fontWeight: 500 }}>
                    {vocab.word} ({vocab.difficulty})
                    <button type="button" onClick={() => handleVocabularyRemove(vocab.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", marginLeft: 4, color: "#1e40af", fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}

            <div style={{ position: "relative" }}>
              <input
                type="text"
                value={vocabularySearch}
                onChange={handleVocabularySearch}
                onFocus={() => setShowVocabularyDropdown(true)}
                placeholder="Search and select vocabularies..."
              />
              {showVocabularyDropdown && (
                <div style={{ position: "absolute", zIndex: 10, width: "100%", background: "#fff", border: "1px solid #ddd", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", maxHeight: 220, overflowY: "auto", marginTop: 4 }}>
                  {loadingVocabularies ? (
                    <div style={{ padding: 12, textAlign: "center", color: "#888", fontSize: 13 }}>Loading...</div>
                  ) : filteredVocabularies.length === 0 ? (
                    <div style={{ padding: 12, textAlign: "center", color: "#888", fontSize: 13 }}>
                      {vocabularySearch ? "No vocabularies found" : "Start typing to search"}
                    </div>
                  ) : (
                    filteredVocabularies.map((vocab) => (
                      <button key={vocab.id} type="button" onClick={() => handleVocabularySelect(vocab)}
                        style={{ width: "100%", textAlign: "left", padding: "9px 14px", background: "none", border: "none", borderBottom: "1px solid #f0f0f0", cursor: "pointer", fontSize: 13 }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f8f9fa"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "none"}>
                        <div style={{ fontWeight: 600, color: "#1a1a1a" }}>{vocab.word}</div>
                        <div style={{ fontSize: 12, color: "#666", display: "flex", gap: 6, flexWrap: "wrap", marginTop: 2 }}>
                          <span>{vocab.difficulty}</span>
                          {(Array.isArray(vocab.partOfSpeech) ? vocab.partOfSpeech : [vocab.partOfSpeech]).filter(Boolean).map((pos, i) => (
                            <span key={i} style={{ background: "#dbeafe", color: "#1e40af", padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>{capitalizePos(pos)}</span>
                          ))}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="form-actions" style={{ marginTop: 24 }}>
            <button type="submit" className="adm-btn adm-btn-primary" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Update" : "Create"}
            </button>
            <button type="button" className="adm-btn adm-btn-ghost" onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </form>
      </div>

      {showVocabularyDropdown && (
        <div style={{ position: "fixed", inset: 0, zIndex: 5 }} onClick={() => setShowVocabularyDropdown(false)} />
      )}

      <ImagePickerModal
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onSelect={(url) => { setFormData({ ...formData, imageUrl: url }); setShowImagePicker(false); }}
        defaultImageUse="VOCABULARY"
      />
    </div>
  );
};

export default VocabularyGroupForm;
